// Helpers for the NVA-elimination and kpi_basis blocks the optimization agent now
// returns on /cms/rl-optimize (result and /preview) and /cms/bpr-redesign.
//
// Steps classified non-value-adding in CMS are stripped from the process BEFORE
// either engine searches for a redesign. That single change breaks two assumptions
// the panels used to be built on:
//
//   Rule N1 — a run can improve the process while applying zero heuristics of its
//             own. `applied_heuristics` is empty on such a run, so it can no longer
//             be used to answer "did anything change?" (see hasAnyChange).
//   Rule N2 — the improvement is no longer all the engine's doing. `engine_only`
//             separates the agent's own contribution from the elimination's, and
//             an applied row carrying `nva_eliminated_task_ids` came from CMS value
//             analysis, not from the engine choosing it (see isNvaDrivenRow).

// Only ever true when the backend actually removed something — `applied: false`
// carries no eliminated[] and must render as nothing, not as an empty card.
export function getNvaElimination(result) {
  const nva = result?.nva_elimination;
  return nva && nva.applied === true ? nva : null;
}

export function getKpiBasis(result) {
  return result?.kpi_basis || null;
}

/**
 * The backend's own headline figures. Preferred over recomputing from as_is/to_be:
 * it already resolves which baseline is the honest one (stored process vs. the
 * projected one with sub-processes redesigned) and states so in `basis`/`includes`.
 */
export function getKpiHeadline(result) {
  return getKpiBasis(result)?.headline || null;
}

/**
 * A percent from kpi_basis is authoritative and must be rendered as sent, never
 * recomputed from the as_is/to_be pair beside it: where an elimination and a
 * redesign are compounded, the two effects are measured on different bases and the
 * reported percent is their compound, not (as_is - to_be) / as_is.
 */
export function getHeadlineMetric(headline, key) {
  const m = headline?.[key];
  return m && typeof m === "object" ? m : null;
}

// True when this row's effect came from the CMS value-analysis classification
// rather than from the engine selecting the heuristic itself.
export function isNvaDrivenRow(row) {
  return Array.isArray(row?.nva_eliminated_task_ids) && row.nva_eliminated_task_ids.length > 0;
}

const isEffectivelyZero = (v) => v == null || Math.abs(Number(v)) < 0.005;

/**
 * Rule N2 — did the engine's own search contribute anything beyond the elimination?
 * `engine_only` reads {0, 0} on a run where every gain came from removing NVA steps,
 * which must not be presented as the agent's result.
 */
export function engineContributedNothing(nva) {
  const eo = nva?.engine_only;
  if (!eo) return false;
  return isEffectivelyZero(eo.time_improvement_pct) && isEffectivelyZero(eo.cost_improvement_pct);
}

/**
 * Rule N1 — "did this run change anything?" across every mechanism that can change
 * the process, in the order they became possible: the engine's own heuristics, an
 * NVA elimination, and a sub-process redesign.
 */
export function hasAnyChange({ appliedHeuristics, nva, subprocessRowCount = 0 }) {
  if (Array.isArray(appliedHeuristics) && appliedHeuristics.length > 0) return true;
  if (subprocessRowCount > 0) return true;
  return Boolean(nva);
}

// "cms_nva_elimination_parent" -> "CMS NVA elimination (this process)". Labels for
// kpi_basis.headline.includes[], which names what is baked into the headline figure.
const INCLUDES_LABEL = {
  cms_nva_elimination_parent: "removal of CMS non-value-adding steps",
  bpr_model_redesign: "the model's own redesign",
  projected_subprocess_redesigns: "projected sub-process redesigns",
};

export function describeIncludes(includes) {
  const items = (Array.isArray(includes) ? includes : [])
    .map((k) => INCLUDES_LABEL[k] || String(k).replace(/_/g, " "));
  if (items.length === 0) return null;
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * BPR sends heuristic names in the model's own snake_case vocabulary
 * ("task_elimination") while every other surface uses prose ("Activity
 * elimination"). Left as-is when it already contains a space, so a backend that
 * starts sending prose is not mangled.
 */
export function humanizeHeuristicName(name) {
  const s = String(name || "").trim();
  if (!s || /\s/.test(s)) return s;
  const words = s.replace(/[_-]+/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
